import Permission from "@/models/Documents/Permission";
import { useDocumentStore } from "@/stores/documents";
import { useSettingsStore } from "@/stores/settings";
import { useMeStore } from "@/stores/me";

/**
 * Getters for documents
 * @category Store
 * @subcategory Documents
 * @module Documents
 * @name Getters
 * @type {object}
 * @getter  {Array<object>} DRIVES_I_CAN_COPY_ITEMS_TO=Array of drives current user may copy documents to
 */

export default {
  DRIVEMAP: state => state.driveMap,

  DRIVE_ITEM:
    (state, getters) =>
    ({ driveId, itemId }) => {
      const driveItem = getters["DRIVE_ITEM_FROM_MAP"]({ driveId, itemId });

      if (driveItem && driveItem.name === "root") {
        const sectionForDriveId = getters["DRIVE_ROOT_SECTION"](driveId);
        // Is driveItem actually a sectiondocumentsroot
        if (sectionForDriveId) {
          return getters["SECTION_DRIVE_ROOT_DRIVEITEM"](sectionForDriveId);
        }
      }

      return driveItem;
    },

  DRIVE_ITEM_BREADCRUMB:
    (state, getters) =>
    ({ driveId, itemId }) => {
      const calcDriveId = getters.CALCULATE_ROOT_OR_DRIVE_ID({ driveId });
      const calcItemId = getters.CALCULATE_ROOT_OR_ITEM_ID({ driveId: calcDriveId, itemId });

      if (state.driveBreadCrumbMap[calcDriveId]) {
        return state.driveBreadCrumbMap[calcDriveId][calcItemId];
      }
      return null;
    },

  DRIVE_ITEM_FROM_MAP:
    (state, getters) =>
    ({ driveId, itemId }) => {
      try {
        const calcDriveId = getters.CALCULATE_ROOT_OR_DRIVE_ID({ driveId });
        const calcItemId = getters.CALCULATE_ROOT_OR_ITEM_ID({ driveId: calcDriveId, itemId });

        if (state.driveMap[calcDriveId]) {
          if (state.driveMap[calcDriveId][calcItemId]) {
            return state.driveMap[calcDriveId][calcItemId];
          } else {
            // eslint-disable-next-line no-unused-vars
            for (let [key, value] of Object.entries(state.driveMap[calcDriveId])) {
              const foundChild = value.children.find(c => c.id === calcItemId);
              if (foundChild) {
                return foundChild;
              }
            }
          }
        }
        // eslint-disable-next-line no-empty
      } catch {}
      return null;
    },

  DRIVE_ITEM_CHILDREN:
    (state, getters) =>
    ({ driveId, itemId }) => {
      const driveItem = getters["DRIVE_ITEM"]({ driveId, itemId });

      if (driveItem && driveItem.children) {
        return driveItem.children;
      }
      return null;
    },

  DRIVE_ITEM_CHILDREN_FOLDERS:
    (state, getters) =>
    ({ driveId, itemId }) => {
      const calcDriveId = getters.CALCULATE_ROOT_OR_DRIVE_ID({ driveId });
      const calcItemId = getters.CALCULATE_ROOT_OR_ITEM_ID({ driveId: calcDriveId, itemId });

      const driveItem = getters["DRIVE_ITEM"]({ driveId: calcDriveId, itemId: calcItemId });

      if (driveItem && driveItem.children) {
        const documentStore = useDocumentStore();
        const folders = driveItem.children.filter(ch => Object.prototype.hasOwnProperty.call(ch, "folder"));
        folders.map(f => {
          f.permissions = documentStore.myItemPermission(calcDriveId, f.id);
        });
        return folders;
      }
      return null;
    },

  CALCULATE_ROOT_OR_ITEM_ID:
    state =>
    ({ driveId, itemId }) => {
      if (itemId) {
        return itemId;
      }

      for (let section in state.sectionRootDriveSettingMap) {
        if (state.sectionRootDriveSettingMap[section].driveId === driveId) {
          return state.sectionRootDriveSettingMap[section].itemId;
        }
      }
      const documentStore = useDocumentStore();
      return documentStore.calculateRootOrItemId(driveId);
    },

  CALCULATE_ROOT_OR_DRIVE_ID:
    () =>
    ({ driveId }) => {
      const documentStore = useDocumentStore();
      return documentStore.calculateDriveIdOrMyDrive(driveId);
    },

  DRIVE_ITEMS_SHARED_WITH_ME: state => state.driveItemsSharedWithMe,

  MY_DRIVE_ROOT: () => {
    const documentStore = useDocumentStore();
    return documentStore.myDriveRootId;
  },

  SECTION_DRIVE_ROOT_DRIVEITEM: (state, getters, rootState, rootGetters) => sectionId => {
    const sectionDriveRoot = state.sectionRootDriveSettingMap[sectionId];

    if (!sectionDriveRoot) {
      return null;
    }

    const driveItem = JSON.parse(
      JSON.stringify(
        getters["DRIVE_ITEM_FROM_MAP"]({
          driveId: sectionDriveRoot.driveId,
          itemId: sectionDriveRoot.itemId
        })
      )
    );

    const subDrives = rootGetters["sections/SECTION_SUB_DRIVES"](sectionDriveRoot.driveId);

    if (subDrives) {
      driveItem.children.push(...subDrives);
    }

    return driveItem;
  },

  SECTION_DRIVE_ROOT_SETTING: state => sectionId =>
    state.sectionRootDriveSettingMap[sectionId] ? state.sectionRootDriveSettingMap[sectionId] : {},

  DRIVE_ROOT_SECTION: state => driveId => {
    return Object.keys(state.sectionRootDriveSettingMap).find(
      key => state.sectionRootDriveSettingMap[key].driveId === driveId
    );
  },

  DRIVES_I_CAN_COPY_ITEMS_TO: (state, getters, rootState, rootGetters) => {
    const documentStore = useDocumentStore();
    const meStore = useMeStore();
    const settingsStore = useSettingsStore();

    const driveGroups = [];
    driveGroups.push({
      parentReference: {
        driveId: documentStore.myDriveRootId
      },
      name: "OneDrive"
    });

    if (rootGetters["setup/COPY_DOCUMENTS_TO_SECTIONS_TEACHER"] && meStore.isTeacher) {
      try {
        const sectionDrives = rootGetters["sections/ALL_SECTIONS_DRIVES"].map(d => ({
          parentReference: {
            driveId: d.driveId
          },
          name: d.name,
          additionalData: d.additionalData
        }));

        driveGroups.push(...sectionDrives.sort((a, b) => a.name.localeCompare(b.name, settingsStore.language)));
        // eslint-disable-next-line no-empty
      } catch {}
    }

    // SharePoint folders
    try {
      const sharedDrives = documentStore.sharedDrives.filter(spf => {
        if (spf.canCopyFromRoleRestriction) {
          if (meStore.isStudent && spf.canCopyFromRoleRestriction.indexOf("student") > -1) return true;

          if (meStore.isTeacher && spf.canCopyFromRoleRestriction.indexOf("teacher") > -1) return true;

          if (meStore.isSuperuser && spf.canCopyFromRoleRestriction.indexOf("superuser") > -1) return true;
        }
        return false;
      });

      driveGroups.push(
        ...sharedDrives
          .filter(
            spf =>
              documentStore.myItemPermission(spf.driveId, spf.itemId) &&
              (documentStore.myItemPermission(spf.driveId, spf.itemId).myPermission ===
                Permission.nctPermission.WRITE ||
                documentStore.myItemPermission(spf.driveId, spf.itemId).myPermission === Permission.nctPermission.OWNER)
          )
          .sort((a, b) => a.displayName.localeCompare(b.displayName, settingsStore.language))
          .map(d => ({ name: d.displayName, id: d.itemId, parentReference: { driveId: d.driveId } }))
      );
      // eslint-disable-next-line no-empty
    } catch {}
    return driveGroups;
  },

  PERMISSION_OBJECTS_FOR_PERSONLIST_ON_DRIVEITEM:
    () =>
    ({ persons, driveId, itemId }) => {
      const documentStore = useDocumentStore();
      let permissionForOthers = documentStore.othersItemPermission(driveId, itemId);

      const permissionObjects = persons.map(psn => {
        if (permissionForOthers[psn.extSysApiUserId]) {
          return {
            userId: psn.userId,
            extSysApiUserId: psn.extSysApiUserId,
            emailSchoolAddress: psn.emailSchoolAddress,
            label: psn.name ? psn.name.name : "",
            permissionOnItem: permissionForOthers[psn.extSysApiUserId],
            personAvatar: psn
          };
        } else if (permissionForOthers[psn.emailSchoolAddress]) {
          return {
            userId: psn.userId,
            extSysApiUserId: psn.extSysApiUserId,
            emailSchoolAddress: psn.emailSchoolAddress,
            label: psn.name ? psn.name.name : "",
            permissionOnItem: permissionForOthers[psn.emailSchoolAddress],
            personAvatar: psn
          };
        } else {
          return {
            userId: psn.userId,
            extSysApiUserId: psn.extSysApiUserId,
            emailSchoolAddress: psn.emailSchoolAddress,
            label: psn.name ? psn.name.name : "",
            permissionOnItem: new Permission(),
            personAvatar: psn
          };
        }
      });

      return permissionObjects;
    }
};
